import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Field, reduxForm } from 'redux-form';
import { Button, Grid, Row, FormGroup, Col, FormControl } from 'react-bootstrap';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { connect } from 'react-redux';

import Summary from './Summary';
import DetailsForHost from './DetailsForHost';
import DetailsForGuest from './DetailsForGuest';
import NotFound from '../../routes/notFound/NotFound';
import CommonFormComponent from '../CommonField/CommonFormComponent';
import Link from '../Link';

import validate from './validate';
import submit from './submit';
import messages from '../../locale/jsonMessages';
import { getDynamicName, getDynamicTitle } from '../../helpers/GetDynamicItemName/getDynamicName';

import s from './Cancellation.css';

function CancellationPolicy(props) {
  const { handleSubmit, submitting, pristine, error, userType, data, intl: { formatMessage }, locale, dynamicLanguageData } = props;
  const { guestData, hostData, messageData, checkOut, checkIn, startTime, endTime, listData, dayDifference, dynamicListTitle } = data;
  const { settingsData, transmission, reviewsCount, reviewsStarRating } = listData || {};

  const today = moment().startOf('days');
  let interval, nights, cancelData = {}, placeholder, carType, transmissionLabel, starRatingValue = 0;
  let momentStartDate, momentEndDate;
  const keepReservationUrl = userType === 'owner' ? '/reservation/current' : '/trips/current';

  if (checkIn && checkOut) {
    momentStartDate = moment(parseInt(checkIn)).utc().format('YYYY-MM-DD');
    momentEndDate = moment(parseInt(checkOut)).utc().format('YYYY-MM-DD');
    interval = moment(momentStartDate).diff(today, 'days');
    nights = dayDifference;
  }

  const calculateCancellation = useCallback((interval, nights) => {
    let accomodation, guestFees, remainingNights, policyName, priorDays, policyContent, policyId;
    const cancellation =
      data?.cancellation || data?.listData?.listingData?.cancellation;

    if (cancellation) {
      policyName = cancellation.policyName;
      priorDays = cancellation.priorDays;
      policyId = cancellation.id
      policyContent = cancellation.policyContent;

      if (interval >= cancellation.priorDays) {
        accomodation = cancellation.accommodationPriorCheckIn;
        guestFees = cancellation.guestFeePriorCheckIn;
      } else if (interval < cancellation.priorDays && interval > 0) {
        accomodation = cancellation.accommodationBeforeCheckIn;
        guestFees = cancellation.guestFeeBeforeCheckIn;
        remainingNights = nights - 1;
      } else {
        accomodation = cancellation.accommodationDuringCheckIn;
        guestFees = cancellation.guestFeeDuringCheckIn;
        remainingNights = (nights - 1) + interval;
      }
    }

    return { policyName, accomodation, guestFees, remainingNights, interval, nights, priorDays, policyContent, policyId };
  }, [data]);

  const handleCancel = useCallback(async () => {
    await handleSubmit();
  }, [handleSubmit]);

  const renderFormControlTextArea = ({ input, label, meta: { touched, error }, className }) => (
    <FormGroup>
      <FormControl
        {...input}
        className={className}
        componentClass="textarea"
        placeholder={label}
      />
      {touched && error && (
        <span className={cx(s.errorMessage, s.spaceInline)}>
          {formatMessage(error)}
        </span>
      )}
    </FormGroup>
  );

  if (guestData && hostData && messageData && listData) {
    cancelData = calculateCancellation(interval, nights);

    placeholder =
      userType === 'owner'
        ? formatMessage(messages.cancelReasonHost)
        : formatMessage(messages.cancelReasonGuest);

    carType = getDynamicName({ value: settingsData[0]?.listsettings, locale, data: dynamicLanguageData });
    carType = carType?.itemName;
    transmissionLabel = transmission === '1'
      ? formatMessage(messages.automatic)
      : formatMessage(messages.manual);

    if (reviewsCount > 0 && reviewsStarRating > 0) {
      starRatingValue = Math.round(reviewsStarRating / reviewsCount);
    }

    let dynamicTitleContent, dynamicTitle;

    if (dynamicListTitle) {
      dynamicTitleContent = JSON.parse(dynamicListTitle)
      dynamicTitle = getDynamicTitle({ data: dynamicTitleContent, locale, id: data?.listId });
    }

    return (
      <div>
        <Grid>
          <Row className={s.landingContaiconfirmationCodener}>
            <form onSubmit={handleSubmit(submit)}>
              <Col xs={12} sm={7} md={7} lg={7}>
                <Summary
                  userType={userType}
                  profileData={userType === 'owner' ? guestData : hostData}
                  guests={data.guests}
                  nights={nights}
                  interval={interval}
                  cancelData={cancelData}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  startTime={startTime}
                  endTime={endTime}
                  hostData={hostData}
                />
                <div className={s.spaceBottom2}>
                  <Field
                    inputClass={cx(s.textAreaInput, s.commonMediumText)}
                    name="message"
                    component={CommonFormComponent}
                    placeholder={placeholder}
                    componentClass="textarea"
                  />
                </div>
                <p className={cx(s.spaceBottom5, s.commonContentText, s.fontWeightNormal)}>
                  {userType === 'owner'
                    ? <FormattedMessage {...messages.reservationCancel} />
                    : <FormattedMessage {...messages.tripCancel} />}
                </p>
                <FormGroup>
                  <div className={cx(s.cancellationBtnSection, s.dFlex)}>
                    <Link
                      className={cx(s.btnPrimaryBorder, s.btnMediumSpace, s.spaceRight2, s.mobMarginBtn, s.textAlignCenter, s.displayinlineBlock, 'spaceRight2RTL')}
                      to={keepReservationUrl}
                    >
                      {userType === 'owner'
                        ? <FormattedMessage {...messages.keepReservation} />
                        : <FormattedMessage {...messages.keepYourTrip} />}
                    </Link>

                    <Button
                      className={cx(s.btnPrimary, s.btnMediumSpace, s.cancelReservationBtn)}
                      onClick={handleCancel}
                    >
                      {userType === 'owner'
                        ? <FormattedMessage {...messages.cancelYourReservation} />
                        : <FormattedMessage {...messages.cancelYourTrip} />}
                    </Button>
                  </div>
                </FormGroup>
              </Col>

              {userType === 'owner' && (
                <DetailsForHost
                  userType={userType}
                  firstName={guestData.firstName}
                  guestEmail={guestData.userData.email}
                  hostName={hostData.firstName}
                  profileId={hostData.profileId}
                  picture={hostData.picture}
                  checkIn={data.checkIn}
                  checkOut={data.checkOut}
                  guests={data.guests}
                  title={dynamicTitle || (data.listTitle || listData.title)}
                  discount={data.discount || 0}
                  listId={data.listId}
                  basePrice={data.basePrice}
                  delivery={data.delivery}
                  guestServiceFee={data.guestServiceFee}
                  hostServiceFee={data.hostServiceFee}
                  total={data.total}
                  currency={data.currency}
                  cancelData={cancelData}
                  reservationId={data.id}
                  threadId={data?.messageThreadId?.id ? data?.messageThreadId?.id : data?.messageData?.id}
                  isSpecialPriceAverage={data.isSpecialPriceAverage}
                  hostServiceFeeValue={data.hostServiceFeeValue}
                  hostServiceFeeType={data.hostServiceFeeType}
                  confirmationCode={data.confirmationCode}
                  holdeData={data}
                  startTime={startTime}
                  endTime={endTime}
                  carType={carType}
                  transmission={transmissionLabel}
                  starRatingValue={starRatingValue}
                  promoDiscount={data?.promoDiscount}
                  promoCodeType={data?.promoCodeType}
                />
              )}

              {userType === 'renter' && (
                <DetailsForGuest
                  userType={userType}
                  firstName={hostData.firstName}
                  hostEmail={hostData.userData.email}
                  guestName={guestData.firstName}
                  profileId={hostData.profileId}
                  picture={hostData.picture}
                  checkIn={data.checkIn}
                  checkOut={data.checkOut}
                  guests={data.guests}
                  title={dynamicTitle || (data.listTitle || listData.title)}
                  listId={data.listId}
                  basePrice={data.basePrice}
                  delivery={data.delivery}
                  guestServiceFee={data.guestServiceFee}
                  hostServiceFee={data.hostServiceFee}
                  total={data.total}
                  currency={data.currency}
                  cancelData={cancelData}
                  reservationId={data.id}
                  threadId={data?.messageThreadId?.id ? data?.messageThreadId?.id : data?.messageData?.id}
                  confirmationCode={data.confirmationCode}
                  discount={data.discount || 0}
                  holdeData={data}
                  startTime={startTime}
                  endTime={endTime}
                  carType={carType}
                  transmission={transmissionLabel}
                  starRatingValue={starRatingValue}
                  isSpecialPriceAverage={data.isSpecialPriceAverage}
                  hostServiceFeeValue={data.hostServiceFeeValue}
                  hostServiceFeeType={data.hostServiceFeeType}
                  promoDiscount={data?.promoDiscount}
                  promoCodeType={data?.promoCodeType}
                />
              )}
            </form>
          </Row>
        </Grid>
      </div>
    );
  }

  return <NotFound />;
}

CancellationPolicy.propTypes = {
  formatMessage: PropTypes.any,
  userType: PropTypes.string.isRequired,
  data: PropTypes.object,
};

CancellationPolicy.defaultProps = {
  data: {
    checkIn: null,
    checkOut: null,
  },
};

const mapState = (state) => ({
  locale: state.intl.locale,
  dynamicLanguageData: state?.dynamicListSettings?.data
});

const mapDispatch = {};

export default injectIntl(
  withStyles(s)(
    connect(mapState, mapDispatch)(
      reduxForm({
        form: 'CancellationForm',
        validate,
        onSubmit: submit,
      })(CancellationPolicy)
    )
  )
);